import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Field, Control, Label, Input, Column } from "rbx";

import FilterButtons from "../../../components/FilterButtons";
import {
  VehicleMultiSelect,
  SystemCodeSelect,
  EmployeeSelect,
  ItemCategorySelect,
  SystemCodeMultipleSelect
} from "../../../components/inputs";
import { useSystemCodes } from "../../../utils/hooks";

const INITIAL_FILTER_STATE = {
  WOStatusRecIdRef_in: [],
  VehicleRecIdRef_in: []
};

function WorkOrderFilters({ filters, onFilter, onClose, onReset }) {
  const SystemCodesVehicles = useSystemCodes({
    Category: "Fleet",
    CodeName: "VehicleStatus",
    CodeDescription_not: "Terminated"
  });
  const [inputs, setInputs] = useState({
    ...filters,
    CreatedDateTime_gte: new Date(filters.CreatedDateTime_gte),
    WOStatusRecIdRef_in: filters.WOStatusRecIdRef_in || [],
    VehicleRecIdRef_in: filters.VehicleRecIdRef_in || []
  });

  const handleChange = e => {
    const updates = { ...inputs };
    if (e.value && !e.target && e.name) {
      if (e.name === "WOStatusRecIdRef_in") {
        const currSelected = [...updates.WOStatusRecIdRef_in];
        const currIndex = currSelected.indexOf(parseInt(e.value));
        if (currIndex > -1) {
          currSelected.splice(currIndex, 1);
        } else {
          currSelected.push(parseInt(e.target.value));
        }
        updates[e.name] = currSelected;
      } else if (e.name === "VehicleRecIdRef_in") {
        const currSelected = [...updates.VehicleRecIdRef_in];
        const currIndex = currSelected.indexOf(parseInt(e.value));
        if (currIndex > -1) {
          currSelected.splice(currIndex, 1);
        } else {
          currSelected.push(parseInt(e.target.value));
        }
        updates[e.name] = currSelected;
      } else {
        updates[e.name] = e;
      }
    } else {
      updates[e.target.name] =
        e.target.name.includes("ItemCatgRecIdRef") ||
        e.target.name.includes("MechanicRecIdRef") ||
        e.target.name.includes("WOTypeRecIdRef")
          ? parseInt(e.target.value)
          : e.target.value;
    }
    setInputs(prev => ({ ...prev, ...updates }));
  };

  const handleDateChange = e => {
    const updates = { ...inputs };

    const { value } = e.target;
    updates[e.target.name] = value;

    setInputs({ ...updates });
  };
  const handleClose = () => onClose(onFilter(inputs));
  const handleCancel = () => onClose();
  const handleReset = () =>
    onReset ? onReset() : onClose(onFilter({ ...INITIAL_FILTER_STATE }));
  return (
    <div style={{ width: 800, maxWidth: "90vw" }}>
      <Column.Group>
        <Column>
          <Field>
            <Control>
              <Label>Work Order Greater Than</Label>
              <Input
                name="WorkOrder_gte"
                type="number"
                value={inputs.WorkOrder_gte}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control>
              <Label>Work Order Less Than</Label>
              <Input
                name="WorkOrder_lte"
                type="number"
                value={inputs.WorkOrder_lte}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
      </Column.Group>
      <Column.Group>
        <Column>
          {SystemCodesVehicles.data &&
            !SystemCodesVehicles.loading &&
            Array.isArray(SystemCodesVehicles.data) &&
            SystemCodesVehicles.data.length > 0 && (
              <VehicleMultiSelect
                isMulti
                Vehicle={inputs.Vehicle}
                label="Vehicle"
                name="VehicleRecIdRef_in"
                value={inputs.VehicleRecIdRef_in}
                vehicleStatus={SystemCodesVehicles.data.map(s => s.RecId)}
                onChange={handleChange}
              />
            )}
        </Column>
      </Column.Group>
      <Column.Group>
        <Column>
          <Field>
            <Control expanded>
              <SystemCodeMultipleSelect
                label="Work Order Status"
                lookup={{ Category: "Fleet", Code: "WOStatus" }}
                name="WOStatusRecIdRef_in"
                value={inputs.WOStatusRecIdRef_in || []}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
        <Column>
          <Field>
            <Control expanded>
              <SystemCodeSelect
                icon="certificate"
                label="Work Order Type"
                lookup={{ Category: "Fleet", Code: "WOType" }}
                name="WOTypeRecIdRef"
                value={inputs.WOTypeRecIdRef}
                valueKey="RecId"
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
      </Column.Group>
      <Column.Group>
        <Column>
          <ItemCategorySelect
            icon="globe"
            label="Item Category"
            name="ItemCatgRecIdRef"
            value={inputs.ItemCatgRecIdRef}
            onChange={handleChange}
          />
        </Column>
        <Column>
          <Field>
            <Control expanded>
              <EmployeeSelect
                icon="globe"
                label="Mechanic"
                name="MechanicRecIdRef"
                value={inputs.MechanicRecIdRef}
                variables={{
                  where: { MechanicFlag: true },
                  orderByMulti: ["LastName_ASC"],
                  orderBy: "LastName_ASC"
                }}
                onChange={handleChange}
              />
            </Control>
          </Field>
        </Column>
      </Column.Group>
      <Column.Group>
        <Column>
          <Control>
            <Label>Start Scheduled Date</Label>
            <Input
              name="WODate_gte"
              type="date"
              value={
                inputs.WODate_gte
                  ? moment(inputs.WODate_gte).format("YYYY-MM-DD")
                  : ""
              }
              onChange={handleDateChange}
            />
          </Control>
        </Column>
        <Column>
          <Control>
            <Label>End Scheduled Date</Label>
            <Input
              name="WODate_lte"
              type="date"
              value={
                inputs.WODate_lte
                  ? moment(inputs.WODate_lte).format("YYYY-MM-DD")
                  : ""
              }
              onChange={handleDateChange}
            />
          </Control>
        </Column>
      </Column.Group>
      <Column.Group>
        <Column>
          <Control>
            <Label>Start Completed Date</Label>
            <Input
              name="CompletedDate_gte"
              type="date"
              value={
                inputs.CompletedDate_gte
                  ? moment(inputs.CompletedDate_gte).format("YYYY-MM-DD")
                  : ""
              }
              onChange={handleDateChange}
            />
          </Control>
        </Column>
        <Column>
          <Control>
            <Label>End Completed Date</Label>
            <Input
              name="CompletedDate_lte"
              type="date"
              value={
                inputs.CompletedDate_lte
                  ? moment(inputs.CompletedDate_lte).format("YYYY-MM-DD")
                  : ""
              }
              onChange={handleDateChange}
            />
          </Control>
        </Column>
      </Column.Group>
      <Column.Group>
        <Column>
          <Control>
            <Label>Start Created Date</Label>
            <Input
              name="CreatedDateTime_gte"
              type="date"
              value={
                inputs.CreatedDateTime_gte
                  ? moment(inputs.CreatedDateTime_gte).format("YYYY-MM-DD")
                  : ""
              }
              onChange={handleDateChange}
            />
          </Control>
        </Column>
        <Column>
          <Control>
            <Label>End Created Date</Label>
            <Input
              name="CreatedDateTime_lte"
              type="date"
              value={
                inputs.CreatedDateTime_lte
                  ? moment(inputs.CreatedDateTime_lte).format("YYYY-MM-DD")
                  : ""
              }
              onChange={handleDateChange}
            />
          </Control>
        </Column>
      </Column.Group>
      <hr />
      <FilterButtons
        handleCancel={handleCancel}
        handleClose={handleClose}
        handleReset={handleReset}
      />
    </div>
  );
}
WorkOrderFilters.propTypes = {
  filters: PropTypes.object,
  onFilter: PropTypes.func,
  onClose: PropTypes.func,
  onReset: PropTypes.func
};
WorkOrderFilters.defaultProps = {
  filters: {}
};
export default WorkOrderFilters;
